import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useDebounceEffect } from 'ahooks';
import { Col, Row, Space, Upload as AntdUpload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  Button,
  EllipsisSpan,
  Form,
  Input,
  Key,
  message,
  Modal,
  Paging,
  Radio,
  Select,
  Tag,
  Table,
  Tabs,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
  Wrapper,
} from '@maxtropy/components';
import ConfigModal from './components/ConfigModal';
import { energyTabs, scopeDimensionMap, scopeTabs } from './config';
import { DimensionKey, MediumType, RecordMap, ResilienceRecord, ScopeKey, SelectOption } from './types';
import { deleteConfigs, fetchConfigOptions, fetchConfigRecords, fetchModalOptions, saveConfig } from './service';
import { getCurrentConfig, getCurrentScope, renderCellValue } from './utils';
import styles from './index.module.scss';

const electricityFilters = [
  { name: 'codeOrName', label: '编号/名称' },
  { name: 'circuitStart', label: '回路起点' },
  { name: 'circuitEnd', label: '回路终点' },
];

const EnergyResilienceConfig = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [filterForm] = Form.useForm();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [updateState, updateFn] = useUpdate();
  const [activeEnergy, setActiveEnergy] = useState(MediumType.Electricity);
  const [activeScope, setActiveScope] = useState(ScopeKey.Factory);
  const [records, setRecords] = useState<ResilienceRecord[]>([]);
  const [filters, setFilters] = useState<RecordMap>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ResilienceRecord>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optionsByField, setOptionsByField] = useState<Record<string, SelectOption[]>>({});
  const [modalOptionsByField, setModalOptionsByField] = useState<Record<string, SelectOption[]>>({});

  const current = useMemo(() => {
    const scope = getCurrentScope(activeEnergy, activeScope);
    const dimension = activeEnergy === MediumType.Electricity ? DimensionKey.Line : scopeDimensionMap[scope];
    const importQuery = new URLSearchParams({
      dimension,
      mediumType: String(activeEnergy),
    }).toString();

    return {
      scope,
      dimension,
      config: getCurrentConfig(activeEnergy, activeScope),
      isFactory: activeEnergy !== MediumType.Electricity && scope === ScopeKey.Factory,
      importQuery,
      scopeTabs: [
        ...scopeTabs,
        ...(activeEnergy === MediumType.CompressedAir ? [{ key: ScopeKey.Compressor, label: '空压机' }] : []),
      ],
    };
  }, [activeEnergy, activeScope]);

  useEffect(() => {
    filterForm.resetFields();
    setFilters({});
    setSelectedRowKeys([]);
    setPageOffset(1);
  }, [activeEnergy, activeScope, filterForm, setPageOffset]);

  useEffect(() => {
    fetchConfigOptions({
      activeEnergy,
      currentDimension: current.dimension,
      currentMediumType: activeEnergy,
      currentScope: current.scope,
      isFactory: current.isFactory,
    }).then(setOptionsByField);
  }, [activeEnergy, current.dimension, current.scope, current.isFactory, updateState]);

  useEffect(() => {
    if (!modalVisible) return;

    fetchModalOptions({ activeEnergy, currentScope: current.scope })
      .then(options => setModalOptionsByField({ ...optionsByField, ...options }))
      .catch(() => setModalOptionsByField(optionsByField));
  }, [activeEnergy, current.scope, modalVisible, optionsByField]);

  useDebounceEffect(
    () => {
      setLoading(true);
      fetchConfigRecords({
        activeEnergy,
        currentDimension: current.dimension,
        currentMediumType: activeEnergy,
        currentScope: current.scope,
        filters,
        isFactory: current.isFactory,
        pageOffset,
        pageSize,
      })
        .then(({ records: nextRecords, total }) => {
          setRecords(nextRecords);
          setTotalCount(total);
        })
        .finally(() => setLoading(false));
    },
    [activeEnergy, activeScope, current, filters, pageOffset, pageSize, updateState],
    { wait: 300 }
  );

  const handleEnergyChange = (key: string) => {
    const nextEnergy = Number(key) as MediumType;
    setActiveEnergy(nextEnergy);
    if (nextEnergy !== MediumType.CompressedAir && activeScope === ScopeKey.Compressor) {
      setActiveScope(ScopeKey.Factory);
    }
  };

  const handleFilterChange = (name: string, value: unknown) => {
    setPageOffset(1);
    setSelectedRowKeys([]);
    setFilters({ ...filterForm.getFieldsValue(), [name]: value } as RecordMap);
  };

  const handleDelete = (ids: Key[]) => {
    if (!ids.length) return message.warning('请先选择要删除的数据');

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${ids.length} 条数据吗？删除后不可恢复。`,
      onOk: () =>
        deleteConfigs({
          currentDimension: current.dimension,
          currentMediumType: activeEnergy,
          ids,
        }).then(() => {
          message.success('删除成功');
          setSelectedRowKeys([]);
          updateFn();
        }),
    });
  };

  const handleSubmit = (values: Record<string, unknown>) => {
    setSaving(true);

    saveConfig({
      activeEnergy,
      currentMediumType: activeEnergy,
      currentScope: current.scope,
      editingRecord,
      optionsByField: modalOptionsByField,
      values,
    })
      .then(() => {
        setModalVisible(false);
        message.success(editingRecord ? '编辑成功' : '新增成功');
        setSelectedRowKeys([]);
        updateFn();
      })
      .finally(() => setSaving(false));
  };

  const handleImport = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'error') {
      Modal.error({ title: '导入失败！' });
      return;
    }
    if (info.file.status !== 'done') return;

    const response = info.file.response;
    if (response?.code === 0) {
      message.success('导入成功');
      updateFn();
      return;
    }
    Modal.error({ title: response?.msg || '导入失败！' });
  };

  const selectProps = (name: string) => ({
    allowClear: true,
    showSearch: true,
    optionFilterProp: 'label',
    placeholder: '请选择',
    options: optionsByField[name],
    onChange: (value: unknown) => handleFilterChange(name, value),
  });

  const renderInputFilter = (name: string, label: string) => (
    <Col span={6} key={name}>
      <Form.Item name={name} label={label}>
        <Input placeholder="请输入" onChange={event => handleFilterChange(name, event.target.value)} />
      </Form.Item>
    </Col>
  );

  const renderSelectFilter = (name: string, label: string) => (
    <Col span={6} key={name}>
      <Form.Item name={name} label={label}>
        <Select {...selectProps(name)} />
      </Form.Item>
    </Col>
  );

  const renderScopeFilters = () => {
    if (activeEnergy === MediumType.Electricity) {
      return electricityFilters.map(item => renderInputFilter(item.name, item.label));
    }

    if (current.scope === ScopeKey.Area) {
      return [renderSelectFilter('areaName', '区域名称'), renderSelectFilter('gasSources', '气体来源')];
    }

    if (current.scope === ScopeKey.Outlet) {
      return [renderSelectFilter('outletName', '出口点位名称'), renderSelectFilter('gasSources', '气体来源')];
    }

    if (current.scope === ScopeKey.Station) return renderSelectFilter('stationName', '站房名称');
    if (current.scope === ScopeKey.Compressor) return renderSelectFilter('compressorName', '空压机名称');
    return null;
  };

  const renderFilters = () => {
    if (activeEnergy !== MediumType.Electricity && current.isFactory) return null;

    return (
      <div className={styles.filters}>
        <Form form={filterForm} isOrigin>
          <Row gutter={16}>{renderScopeFilters()}</Row>
        </Form>
      </div>
    );
  };

  const columns = [
    ...current.config.fields.map(field => ({
      title: field.unit ? `${field.label}（${field.unit}）` : field.label,
      dataIndex: field.dataIndex ?? field.name,
      ellipsis: true,
      render: (value: unknown) => <EllipsisSpan value={renderCellValue(value)} />,
    })),
    ...(activeEnergy === MediumType.Electricity
      ? [
          {
            title: '关联区域',
            dataIndex: 'areaDataList',
            width: 220,
            render: (_: unknown, record: ResilienceRecord) => {
              const areaDataList = (record.areaDataList as Array<{ areaName?: string | number }> | undefined) ?? [];
              return areaDataList.length ? (
                <Space size={[4, 4]} wrap>
                  {areaDataList.map((item, index) => (
                    <Tag key={`${item.areaName}-${index}`}>{item.areaName}</Tag>
                  ))}
                </Space>
              ) : (
                '--'
              );
            },
          },
        ]
      : []),
    {
      title: '操作',
      key: 'action',
      width: current.isFactory ? 80 : 140,
      fixed: 'right' as const,
      render: (_: unknown, record: ResilienceRecord) => (
        <Space size={16}>
          <Button
            type="link"
            onClick={() => {
              setEditingRecord(record);
              setModalVisible(true);
            }}
          >
            编辑
          </Button>
          {!current.isFactory && (
            <Button type="link" danger onClick={() => handleDelete([record.id])}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const renderTable = () => {
    const baseTableProps = {
      rowKey: 'id',
      loading,
      columns,
      dataSource: records,
    };

    if (current.isFactory) {
      return (
        <Table
          {...baseTableProps}
          sticky={{ getContainer: () => document.querySelector('.central-menu-content') as HTMLElement }}
        />
      );
    }

    return (
      <Table
        {...baseTableProps}
        rowSelection={{
          selectedRowKeys,
          onChange: rowKeys => setSelectedRowKeys(rowKeys as Key[]),
          preserveSelectedRowKeys: true,
        }}
      />
    );
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]} hasPadding={false}>
      <div className={styles.content}>
        <Tabs
          items={energyTabs.map(item => ({ ...item, key: String(item.key) }))}
          activeKey={String(activeEnergy)}
          selectedLinePlacement="top"
          onChange={handleEnergyChange}
        />

        <div className={styles.configControls}>
          {activeEnergy !== MediumType.Electricity && (
            <div className={styles.scopeSwitch}>
              <Radio.Group
                value={activeScope}
                optionType="button"
                buttonStyle="solid"
                onChange={event => setActiveScope(event.target.value)}
              >
                {current.scopeTabs.map(item => (
                  <Radio.Button key={item.key} value={item.key}>
                    {item.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          )}
          {renderFilters()}
        </div>

        {!current.isFactory && (
          <Space size="small" className={styles.actions} style={{ marginBottom: 10 }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingRecord(undefined);
                setModalVisible(true);
              }}
            >
              新增
            </Button>
            <AntdUpload
              action={`/api/v2/energy-resilience/config/import?${current.importQuery}`}
              accept=".xlsx,.xls"
              maxCount={1}
              showUploadList={false}
              onChange={handleImport}
            >
              <Button type="primary">导入</Button>
            </AntdUpload>
            <Button
              type="link"
              style={{ padding: '0 8px' }}
              href={`/api/v2/energy-resilience/config/import-template?${current.importQuery}`}
            >
              下载模板
            </Button>
            <Button disabled={!selectedRowKeys.length} onClick={() => handleDelete(selectedRowKeys)}>
              批量删除
            </Button>
          </Space>
        )}

        <div className={styles.tableWrapper}>
          {renderTable()}
          {!current.isFactory && <Paging pagingInfo={pagingInfo} />}
        </div>
      </div>

      <ConfigModal
        open={modalVisible}
        energy={activeEnergy}
        scope={current.scope}
        editingRecord={editingRecord}
        optionsByField={modalOptionsByField}
        confirmLoading={saving}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleSubmit}
      />
    </Wrapper>
  );
};

export default EnergyResilienceConfig;
